import {
  DefaultRequestBody,
  MockedRequest,
  rest,
  RestHandler,
  RestRequest,
} from "msw";
import {
  DEFAULT_APP_CONFIG,
  QA_APP_CONFIG,
  PROJECTS_ENDPOINT,
  PACKAGES_ENDPOINT,
  NOTIFICATIONS_ENDPOINT,
  LOCAL_BASE_URL,
} from "../constants/urls.constants";
import ErrorConstants from "../../api/constants/error.constants";
import { PersistentMockProjectsService } from "./api.service";

type MSWHandler = RestHandler<MockedRequest<DefaultRequestBody>>;

class MockHandlers {
  private _handlers: MSWHandler[];
  private _mockAll: boolean;

  public service: PersistentMockProjectsService;
  public requiredAccessToken: string | null;
  public requireAuth: boolean;

  constructor() {
    this.service = new PersistentMockProjectsService({
      createNotFoundProjects: true,
    });
    this._mockAll = false;
    this._handlers = this.buildHandlers();
    this.requiredAccessToken = null;
    this.requireAuth = false;
  }

  public get handlers(): Array<MSWHandler> {
    return this._handlers;
  }

  private validateAuth<TBody, TParams>(
    req: RestRequest<TBody, TParams>
  ): boolean {
    if (this.requireAuth) {
      const authToken = req.headers.get("authorization");
      if (
        !authToken ||
        (authToken !== this.requiredAccessToken &&
          this.requiredAccessToken !== null)
      ) {
        return false;
      }
    }
    return true;
  }

  private buildHandlers(): MSWHandler[] {
    const handlersLocal = this.buildHandlersForBaseUrl(LOCAL_BASE_URL);
    const handlersDev = this.buildHandlersForBaseUrl(DEFAULT_APP_CONFIG.apiUrl);
    const handlersQA = this.buildHandlersForBaseUrl(QA_APP_CONFIG.apiUrl);

    let handlers = handlersDev.concat(handlersQA).concat(handlersLocal);

    if (this.mockAll) {
      handlers = handlers.concat(this.buildTestsOnlyHandlers());
    }
    return handlers;
  }

  private buildHandlersForBaseUrl(baseUrl: string): MSWHandler[] {
    const handlers = [
      rest.get(
        `${baseUrl}${PROJECTS_ENDPOINT}/:projectId`,
        async (req, res, ctx) => {
          console.log(`Received request at endpoint: ${req.url}`);
          if (!this.validateAuth(req)) {
            return res(ctx.status(401));
          }

          const testHttpError = req.url.searchParams.get("testHttpError");
          if (testHttpError) {
            return res(
              ctx.status(500),
              ctx.json(ErrorConstants.INTERNAL_SERVER_ERROR)
            );
          }

          let { projectId } = req.params;
          if (!projectId) {
            return res(
              ctx.status(404),
              ctx.json(ErrorConstants.NOT_FOUND_ERROR)
            );
          }
          projectId = decodeURIComponent(projectId);
          const project = await this.service.getProjectDetails({
            projectId: projectId ?? "",
            packageId: "",
          });

          return res(ctx.status(200), ctx.json(project));
        }
      ),
      rest.get(
        `${baseUrl}${PROJECTS_ENDPOINT}/:projectId${PACKAGES_ENDPOINT}`,
        async (req, res, ctx) => {
          try {
            console.log(`Received request at endpoint: ${req.url}`);
            if (!this.validateAuth(req)) {
              return res(
                ctx.status(401),
                ctx.json(ErrorConstants.UNAUTHORIZED_ERROR)
              );
            }

            let { projectId } = req.params;

            if (!projectId) {
              return res(ctx.status(404));
            }

            projectId = decodeURIComponent(projectId);
            const testHttpError = req.url.searchParams.get("testHttpError");
            if (testHttpError) {
              return res(
                ctx.status(500),
                ctx.json(ErrorConstants.INTERNAL_SERVER_ERROR)
              );
            }

            const packages = await this.service.getProjectPackages({
              projectId: projectId,
            });

            return res(ctx.status(200), ctx.json(packages));
          } catch (error) {
            return res(ctx.status(500), ctx.json(error));
          }
        }
      ),
      rest.get(
        `${baseUrl}${PROJECTS_ENDPOINT}/:projectId${PACKAGES_ENDPOINT}/:packageId`,
        async (req, res, ctx) => {
          console.log(`Received request at endpoint: ${req.url}`);
          if (!this.validateAuth(req)) {
            return res(
              ctx.status(401),
              ctx.json(ErrorConstants.UNAUTHORIZED_ERROR)
            );
          }

          let { projectId, packageId } = req.params;

          if (!projectId || !packageId) {
            return res(ctx.status(404));
          }

          projectId = decodeURIComponent(projectId);
          packageId = decodeURIComponent(packageId);
          const testHttpError = req.url.searchParams.get("testHttpError");
          if (testHttpError) {
            return res(
              ctx.status(500),
              ctx.json(ErrorConstants.INTERNAL_SERVER_ERROR)
            );
          }

          console.log(`Getting package details for ${projectId}:${packageId}`);
          console.log(
            `createNotFoundProjects: ${this.service.createNotFoundProjects}`
          );
          const packageX = await this.service.getPackageDetails({
            projectId: projectId,
            packageId: packageId,
          });

          return res(ctx.status(200), ctx.json(packageX));
        }
      ),
      rest.get(
        `${baseUrl}${PROJECTS_ENDPOINT}/:projectId${PACKAGES_ENDPOINT}/:packageId${NOTIFICATIONS_ENDPOINT}`,
        async (req, res, ctx) => {
          console.log(`Received request at endpoint: ${req.url}`);
          if (!this.validateAuth(req)) {
            return res(
              ctx.status(401),
              ctx.json(ErrorConstants.UNAUTHORIZED_ERROR)
            );
          }

          let { projectId, packageId } = req.params;

          if (!projectId || !packageId) {
            return res(ctx.status(404));
          }

          projectId = decodeURIComponent(projectId);
          packageId = decodeURIComponent(packageId);
          const testHttpError = req.url.searchParams.get("testHttpError");
          if (testHttpError) {
            return res(
              ctx.status(500),
              ctx.json(ErrorConstants.INTERNAL_SERVER_ERROR)
            );
          }

          const notifications = await this.service.getPackageNotifications({
            projectId: projectId,
            packageId: packageId,
          });

          return res(ctx.status(200), ctx.json(notifications));
        }
      ),
    ];

    return handlers;
  }

  private buildTestsOnlyHandlers(): MSWHandler[] {
    const handlersLocal = this.buildTestsOnlyHandlersForBaseUrl(LOCAL_BASE_URL);
    const handlersDev = this.buildTestsOnlyHandlersForBaseUrl(
      DEFAULT_APP_CONFIG.apiUrl
    );
    const handlersQA = this.buildTestsOnlyHandlersForBaseUrl(
      QA_APP_CONFIG.apiUrl
    );

    return handlersDev.concat(handlersQA).concat(handlersLocal);
  }

  private buildTestsOnlyHandlersForBaseUrl(baseUrl: string): MSWHandler[] {
    const parseIntHelper = (
      value: string | null
    ): [number | undefined, boolean] => {
      if (value) {
        const num = +value;
        if (!isNaN(num)) {
          return [num, true];
        } else {
          return [undefined, false];
        }
      } else {
        return [undefined, true];
      }
    };

    return [
      rest.get(`${baseUrl}${PROJECTS_ENDPOINT}`, async (req, res, ctx) => {
        console.log(`Received request at endpoint: ${req.url}`);
        if (!this.validateAuth(req)) {
          return res(
            ctx.status(401),
            ctx.json(ErrorConstants.UNAUTHORIZED_ERROR)
          );
        }

        const offset = parseIntHelper(req.url.searchParams.get("offset"));
        const limit = parseIntHelper(req.url.searchParams.get("limit"));

        if (!offset[1] || !limit[1]) {
          return res(
            ctx.status(400),
            ctx.json(ErrorConstants.BAD_REQUEST_ERROR)
          );
        }

        const search = req.url.searchParams.get("search") ?? undefined;

        const testHttpError = req.url.searchParams.get("testHttpError");
        if (testHttpError) {
          return res(
            ctx.status(500),
            ctx.json(ErrorConstants.INTERNAL_SERVER_ERROR)
          );
        }

        return res(
          ctx.status(200),
          ctx.json(
            await this.service.getProjects({
              limit: limit[0],
              offset: offset[0],
              search,
            })
          )
        );
      }),
    ];
  }

  public get mockAll(): boolean {
    return this._mockAll;
  }

  public set mockAll(value: boolean) {
    if (this._mockAll != value) {
      this._mockAll = value;
      this._handlers = this.buildHandlers();
    }
  }
}

const handlersHolder = new MockHandlers();
handlersHolder.requireAuth = false;

export default handlersHolder;
